import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useConfirmation } from 'base/common';
import { selectModalLoader } from 'pages/sessions/components/sessions-create/selector';
import { createSessionActions } from 'pages/sessions/components/sessions-create/slice';

import {
  ClosableModal, ErrorMessage, PrimaryLoadingButton, SecondaryButton,
} from 'components';

import VideoTrimmer from '../video-trimmer';
import './styles.scss';
import { mediaLibraryAction } from '../../slice';
import { selectMediaModelLoading } from '../../selector';

interface Props {
  isOpen: boolean;
  isMediaTrimming: boolean;
  trimVideoPath: string;
  trimVideoId: string;
  meetingId: string | undefined | null;
  onClose: () => void;
}

const RecordingShareModel = ({
  isOpen, isMediaTrimming, trimVideoPath, trimVideoId, meetingId, onClose,
}: Props) => {
  const dispatch = useDispatch();
  const confirm = useConfirmation();
  const sessionModelLoading = useSelector(selectModalLoader);
  const mediaModelLoading = useSelector(selectMediaModelLoading);

  const [trimData, setTrimData] = React.useState({
    isTrimmed: false,
    startTime: '00:00:00',
    endTime: '00:00:00',
  });

  const loading = sessionModelLoading || mediaModelLoading || trimData.endTime === '00:00:00';

  const handleSaveAndShareRecording = (isShare: boolean) => {
    if (meetingId && trimVideoPath) {
      confirm({
        variant: 'confirm',
        message: isShare ? 'Your recording will be shared to the participants in the session/class and saved in your media library immediately after you click on confirm. If you\'ve trimmed the recording, it will be processed first and then shared and saved.' : 'Your recording will be saved in your media library immediately after you click on confirm. If you\'ve trimmed the recording, it will be processed first and then saved.',
        confirmButtonTxt: 'Confirm',
        cancelButtonTxt: 'Back',
        catchOnCancel: true,
      }).then(async () => {
        try {
          await new Promise((resolve, reject) => {
            dispatch(createSessionActions.saveShareSessionRecording({
              meetingId,
              // eslint-disable-next-line no-useless-escape
              path: trimVideoPath.replace(/^.*\/\/[^\/]+/, ''),
              isShare,
              ...trimData,
              resolve,
              reject,
            }));
            onClose();
          });
        } catch (error) {
          onClose();
        }
      }).catch();
    }
  };

  const handleDiscardRecording = () => {
    confirm({
      variant: 'confirm',
      message: 'Your recording will not be saved in your media library or shared with participants of the live class or session. However, you can still access it through the link provided in the email notifying you that the recording has been processed.',
      confirmButtonTxt: 'Confirm',
      cancelButtonTxt: 'Go back',
    }).then(() => {
      onClose();
    });
  };

  const handleSaveTrimmedMedia = (isDownload: boolean) => {
    if (trimVideoPath) {
      confirm({
        variant: 'confirm',
        message: isDownload ? 'Your download will be shared via a link to your email address once processed after you click on confirm' : 'Your trimmed video will be saved in the media library after you click on confirm and the video has been processed',
        confirmButtonTxt: 'Confirm',
        cancelButtonTxt: 'Back',
        catchOnCancel: true,
      }).then(async () => {
        try {
          await new Promise((resolve, reject) => {
            dispatch(mediaLibraryAction.saveDownloadTrimmedVideo({
              // eslint-disable-next-line no-useless-escape
              path: trimVideoPath.replace(/^.*\/\/[^\/]+/, ''),
              mediaId: trimVideoId,
              ...trimData,
              isDownload,
              resolve,
              reject,
            }));
            onClose();
          });
        } catch (error) {
          onClose();
        }
      }).catch();
    }
  };

  const handleDiscardMedia = () => {
    confirm({
      variant: 'confirm',
      message: 'Your changes will be discarded and the video will be unaffected',
      confirmButtonTxt: 'Confirm',
      cancelButtonTxt: 'Go back',
    }).then(() => {
      onClose();
    });
  };

  React.useEffect(() => {
    if (!isOpen) {
      setTrimData({
        isTrimmed: false,
        startTime: '00:00:00',
        endTime: '00:00:00',
      });
    }
  }, [isOpen]);

  return (
    <ClosableModal
      open={isOpen}
      onClose={() => { onClose(); }}
      title={`${isMediaTrimming ? 'Media Library - Video Trimming' : 'Share or save your session recording'}`}
      subTitle=""
      modalClass="trim-video-modal"
    >
      <>
        <div className="row">
          <div className="col-12 mb-4">
            <VideoTrimmer
              videoUrl={trimVideoPath as string}
              onTrimingSliderChange={(start: string, end: string) => {}}
              onTrimDataChange={setTrimData}
            />
          </div>
        </div>
        {isMediaTrimming ? (
          <>
            <div className="row mt-3">
              <div className="col-12 col-md-6 mb-2 mb-md-0">
                <PrimaryLoadingButton
                  fullWidth
                  onClick={() => { handleSaveTrimmedMedia(false); }}
                  loading={loading}
                  disabled={loading || !trimData.isTrimmed}
                >
                  Save trimmed video
                </PrimaryLoadingButton>
              </div>
              <div className="col-12 col-md-6">
                <PrimaryLoadingButton
                  fullWidth
                  onClick={() => { handleSaveTrimmedMedia(true); }}
                  loading={loading}
                  disabled={loading || !trimData.isTrimmed}
                >
                  Download trimmed video
                </PrimaryLoadingButton>
              </div>
              <div className="mt-2 text-center">
                <ErrorMessage>
                  {/* {attachSessionError} */}
                </ErrorMessage>
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-12 mb-2 mb-md-0">
                <SecondaryButton fullWidth onClick={handleDiscardMedia}>
                  Discard
                </SecondaryButton>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="row mt-3">
              <div className="col-12 col-md-6 mb-2 mb-md-0">
                <PrimaryLoadingButton
                  fullWidth
                  onClick={() => { handleSaveAndShareRecording(true); }}
                  loading={loading}
                  disabled={loading}
                >
                  Save and Share recording
                </PrimaryLoadingButton>
              </div>
              <div className="col-12 col-md-6">
                <PrimaryLoadingButton
                  fullWidth
                  onClick={() => { handleSaveAndShareRecording(false); }}
                  loading={loading}
                  disabled={loading}
                >
                  Save recording
                </PrimaryLoadingButton>
              </div>
              <div className="mt-2 text-center">
                <ErrorMessage>
                  {/* {attachSessionError} */}
                </ErrorMessage>
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-12 mb-2 mb-md-0">
                <SecondaryButton fullWidth onClick={handleDiscardRecording}>
                  Discard recording
                </SecondaryButton>
              </div>
            </div>
          </>
        )}

      </>
    </ClosableModal>
  );
};

export default RecordingShareModel;
